import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { haptic } from '@/shared/lib/telegram';
import {
  Search as SearchIcon,
  Heart,
  Zap,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react';
import { catalogService, type Product } from '@/services/catalog.service';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/shared/ui/Skeleton';
import { EmptyState } from '@/shared/ui/EmptyState';
import { CategoryCard } from '@/features/catalog/CategoryCard';
import { ProductCard } from '@/features/catalog/ProductCard';
import { useT } from '@/i18n';
import { useAuthStore, useRestrictions } from '@/stores/auth.store';

export function HomePage() {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const lang = useAuthStore((s) => s.language);
  const restrictions = useRestrictions();

  const categoriesQ = useQuery({
    queryKey: ['categories'],
    queryFn: catalogService.listCategories,
  });

  const featuredQ = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () => catalogService.listProducts({ featured: true, limit: 10 }),
  });

  return (
    <Layout>
      <div className="pt-2">
        <Hero firstName={user?.firstName ?? null} tagline={t('splash.subtitle')} />

        {/* Quick-access bar — search + favorites. Sticky-ish, always one tap away. */}
        <div className="grid grid-cols-[1fr_auto] gap-2 -mt-2 mb-4">
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex items-center gap-2.5 h-11 px-4 rounded-2xl text-left transition active:scale-[0.99]"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
              boxShadow:
                'inset 0 0 0 1px rgba(255,255,255,0.07), 0 6px 16px rgba(0,0,0,0.3)',
            }}
          >
            <SearchIcon className="h-4 w-4 text-text-tertiary shrink-0" />
            <span className="text-text-tertiary text-[13px] truncate">
              {lang === 'ru'
                ? 'Найти услугу…'
                : lang === 'en'
                ? 'Search services…'
                : 'Xizmat qidirish…'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/favorites')}
            aria-label="Favorites"
            className="inline-flex items-center justify-center h-11 w-11 rounded-2xl transition active:scale-95"
            style={{
              background:
                'linear-gradient(135deg, rgba(244, 63, 94, 0.12), rgba(244, 63, 94, 0.04))',
              boxShadow:
                'inset 0 0 0 1px rgba(244, 63, 94, 0.35), 0 6px 16px rgba(244, 63, 94, 0.18)',
            }}
          >
            <Heart className="h-4 w-4 text-rose-300 fill-rose-400/30" />
          </button>
        </div>

        <TrustBar lang={lang} />

        <section className="mt-6 mb-6">
          <SectionHeader title={t('home.categories')} />
          {categoriesQ.isLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-44" />
              ))}
            </div>
          ) : categoriesQ.data && categoriesQ.data.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
              className="grid grid-cols-2 gap-3"
            >
              {categoriesQ.data.map((c) => (
                <motion.div
                  key={c.id}
                  variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                >
                  <CategoryCard
                    category={c}
                    featured={featuredQ.data?.items}
                    restricted={restrictions.services.includes(c.kind)}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <EmptyState title={t('home.empty')} />
          )}
        </section>

        {featuredQ.data && featuredQ.data.items.length > 0 && (
          <section className="mb-8">
            <SectionHeader
              title={t('home.featured')}
              kicker={lang === 'ru' ? 'Хиты' : lang === 'en' ? 'Top picks' : 'Eng ommabop'}
            />
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
              className="flex flex-col gap-2.5"
            >
              {dedupeByCategory(featuredQ.data.items).map((p) => (
                <motion.div
                  key={p.id}
                  variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
                >
                  <ProductCard product={p} variant="hero" />
                </motion.div>
              ))}
            </motion.div>
          </section>
        )}

        <FooterBadge lang={lang} />
      </div>
    </Layout>
  );
}

/** Keep only the first featured product per category (one card per category in Ommabop section). */
function dedupeByCategory(items: Product[]): Product[] {
  const seen = new Set<string>();
  const out: Product[] = [];
  for (const p of items) {
    if (seen.has(p.categoryId)) continue;
    seen.add(p.categoryId);
    out.push(p);
  }
  return out;
}

/* ------------------- Hero ------------------- */
function Hero({
  firstName,
  tagline,
}: {
  firstName: string | null;
  tagline: string;
}) {
  const initial = (firstName ?? 'U').charAt(0).toUpperCase();
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
      className="relative overflow-hidden rounded-3xl px-5 py-5 ring-1 ring-white/10 mb-5"
      style={{ background: 'linear-gradient(135deg, #1F1147 0%, #2A1B5E 35%, #1C1538 100%)' }}
    >
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#7F77DD]/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-[#D4537E]/30 blur-3xl pointer-events-none" />

      <div className="relative flex items-center gap-3">
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-telegram to-brand-instagram-purple blur-md opacity-60" />
          <div className="relative h-12 w-12 rounded-2xl bg-gradient-to-br from-brand-telegram to-brand-instagram-purple flex items-center justify-center text-white font-bold text-lg shadow-lg ring-1 ring-white/15">
            {initial}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white/70 text-[11px] uppercase tracking-widest mb-0.5">
            Galochka Bot
          </div>
          <div className="text-white font-bold text-lg leading-tight truncate">
            {firstName ? `Salom, ${firstName}!` : 'Xush kelibsiz!'}
          </div>
        </div>
      </div>

      <p className="relative text-white/75 text-sm mt-3 leading-relaxed">{tagline}</p>
    </motion.div>
  );
}

/* ------------------- TrustBar ------------------- */
interface TrustDetails {
  uz: string[];
  ru: string[];
  en: string[];
  titleUz: string;
  titleRu: string;
  titleEn: string;
}

interface TrustTile {
  Icon: typeof Zap;
  iconTone: string; // text color for the icon
  glow: string; // outer box-shadow glow color
  ring: string; // ring/border color
  valueUz: string;
  valueRu: string;
  valueEn: string;
  labelUz: string;
  labelRu: string;
  labelEn: string;
  valueGradient?: string; // optional gradient for the value text
  details: TrustDetails;
}

const TRUST_TILES: TrustTile[] = [
  {
    Icon: Zap,
    iconTone: 'text-amber-300',
    glow: 'rgba(251, 191, 36, 0.35)',
    ring: 'rgba(251, 191, 36, 0.28)',
    valueUz: '5–15 daq',
    valueRu: '5–15 мин',
    valueEn: '5–15 min',
    labelUz: 'Avto-aktivatsiya',
    labelRu: 'Авто-активация',
    labelEn: 'Auto-activation',
    valueGradient: 'linear-gradient(180deg, #FFE066 0%, #FFB347 100%)',
    details: {
      titleUz: 'Avto-aktivatsiya — 5–15 daqiqa',
      titleRu: 'Авто-активация — 5–15 минут',
      titleEn: 'Auto-activation — 5–15 min',
      uz: [
        'Toʻlov tasdiqlangach mahsulot avtomatik faollashadi.',
        '90% buyurtmalar 5 daqiqada, qolgani 15 daqiqaga yetadi.',
        'Tunda, dam olish kunlari ham — operator kerak emas.',
        'Status Mini App ichida real vaqtda yangilanib turadi.',
      ],
      ru: [
        'После подтверждения оплаты товар активируется автоматически.',
        '90% заказов — за 5 минут, остальные — до 15 минут.',
        'Работает круглосуточно, включая выходные.',
        'Статус обновляется в реальном времени прямо в Mini App.',
      ],
      en: [
        'Once payment is approved, the product activates automatically.',
        '90% of orders complete in 5 minutes, the rest within 15.',
        'Works 24/7, including weekends — no operator needed.',
        'Status updates live inside the Mini App.',
      ],
    },
  },
  {
    Icon: ShieldCheck,
    iconTone: 'text-emerald-300',
    glow: 'rgba(16, 185, 129, 0.35)',
    ring: 'rgba(16, 185, 129, 0.28)',
    valueUz: '100%',
    valueRu: '100%',
    valueEn: '100%',
    labelUz: 'Xavfsiz toʻlov',
    labelRu: 'Безопасная оплата',
    labelEn: 'Secure payment',
    valueGradient: 'linear-gradient(180deg, #BBF7D0 0%, #34D399 100%)',
    details: {
      titleUz: '100% xavfsiz toʻlov',
      titleRu: '100% безопасная оплата',
      titleEn: '100% secure payment',
      uz: [
        'TLS/SSL shifrlangan aloqa — kart maʼlumotlari hech qachon ochiq jonatilmaydi.',
        'Toʻlov Uzcard / Humo / Click / Payme orqali bevosita sizdan bank kartasiga oʻtadi.',
        'Mahsulot yetkazib berilmasa — toʻliq pul qaytariladi.',
        'Har bir chek qoʻlda tekshiriladi va imzo bilan tasdiqlanadi.',
        'Kart raqamini biz hech kim bilan ulashmaymiz.',
      ],
      ru: [
        'TLS/SSL шифрование — данные карты никогда не передаются в открытом виде.',
        'Оплата идёт напрямую через Uzcard / Humo / Click / Payme на банковскую карту.',
        'Если товар не был доставлен — полный возврат средств.',
        'Каждый чек проверяется вручную и подписывается оператором.',
        'Номер карты ни с кем не передаём.',
      ],
      en: [
        'TLS/SSL encryption — your card data is never transmitted in plaintext.',
        'Payment goes directly via Uzcard / Humo / Click / Payme to a real bank card.',
        'If the product is not delivered — full refund, no questions.',
        'Every receipt is manually verified and operator-signed.',
        'We never share your card number with anyone.',
      ],
    },
  },
  {
    Icon: Star,
    iconTone: 'text-violet-300',
    glow: 'rgba(167, 139, 250, 0.35)',
    ring: 'rgba(167, 139, 250, 0.28)',
    valueUz: '4.9 ★',
    valueRu: '4.9 ★',
    valueEn: '4.9 ★',
    labelUz: 'Foydalanuvchi reytingi',
    labelRu: 'Рейтинг клиентов',
    labelEn: 'Customer rating',
    valueGradient: 'linear-gradient(180deg, #E9D5FF 0%, #A78BFA 100%)',
    details: {
      titleUz: 'Mijozlar reytingi — 4.9 / 5',
      titleRu: 'Рейтинг клиентов — 4.9 / 5',
      titleEn: 'Customer rating — 4.9 / 5',
      uz: [
        'Reyting buyurtmadan keyin haqiqiy mijozlar tomonidan qoʻyiladi.',
        '10 000+ baholash asosida hisoblanadi.',
        'Reyting har soatda yangilanadi — soxta sharhlar avtomatik chiqarib tashlanadi.',
        'Salbiy sharhga 24 soat ichida shaxsiy javob beriladi.',
      ],
      ru: [
        'Оценку ставят настоящие клиенты после получения заказа.',
        'Рейтинг рассчитан по 10 000+ отзывам.',
        'Обновляется каждый час; накрутка автоматически отсекается.',
        'На негативные отзывы отвечаем лично в течение 24 часов.',
      ],
      en: [
        'Rating is left by real customers after they receive their order.',
        'Computed from 10,000+ verified reviews.',
        'Refreshes hourly — fake reviews are auto-filtered.',
        'Negative reviews get a personal response within 24 hours.',
      ],
    },
  },
  {
    Icon: Users,
    iconTone: 'text-sky-300',
    glow: 'rgba(56, 189, 248, 0.35)',
    ring: 'rgba(56, 189, 248, 0.28)',
    valueUz: '10K+',
    valueRu: '10К+',
    valueEn: '10K+',
    labelUz: 'Mamnun mijoz',
    labelRu: 'Довольных клиентов',
    labelEn: 'Happy customers',
    valueGradient: 'linear-gradient(180deg, #BAE6FD 0%, #38BDF8 100%)',
    details: {
      titleUz: '10 000+ mamnun mijoz',
      titleRu: '10 000+ довольных клиентов',
      titleEn: '10,000+ happy customers',
      uz: [
        'Oʻzbekistondagi eng yirik raqamli xizmatlar mijoz bazasiga ega botlardan birimiz.',
        '2 yildan beri uzluksiz ishlab kelyapmiz.',
        'Har oyda 1 500+ yangi mijoz qoʻshiladi.',
        'Mijozlarning 78% i qaytib buyurtma beradi.',
      ],
      ru: [
        'Одна из крупнейших клиентских баз среди digital-сервис-ботов Узбекистана.',
        'Работаем без перерыва уже больше 2 лет.',
        'Каждый месяц прибавляется 1 500+ новых клиентов.',
        '78% клиентов возвращаются за повторными заказами.',
      ],
      en: [
        "One of Uzbekistan's largest customer bases for digital-service bots.",
        'Running continuously for over 2 years.',
        '1,500+ new customers join every month.',
        '78% of our customers come back for repeat orders.',
      ],
    },
  },
];

function TrustBar({ lang }: { lang: string }) {
  const [active, setActive] = useState<TrustTile | null>(null);
  return (
    <>
      <motion.div
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
        }}
        className="flex gap-1.5 overflow-x-auto hide-scrollbar -mx-4 px-4 mb-2"
      >
        {TRUST_TILES.map((tile) => (
          <TrustChip
            key={tile.labelEn}
            tile={tile}
            lang={lang as 'uz' | 'ru' | 'en'}
            onPress={() => {
              haptic('selection');
              setActive(tile);
            }}
          />
        ))}
      </motion.div>
      <TrustInfoSheet
        tile={active}
        lang={lang as 'uz' | 'ru' | 'en'}
        onClose={() => setActive(null)}
      />
    </>
  );
}

/**
 * Compact gradient chip — icon + value + label inline. Smaller and more
 * elegant than the previous 4-tile grid; horizontal scroll on narrow screens
 * keeps the row from wrapping awkwardly.
 */
function TrustChip({
  tile,
  lang,
  onPress,
}: {
  tile: TrustTile;
  lang: 'uz' | 'ru' | 'en';
  onPress: () => void;
}) {
  const Icon = tile.Icon;
  const value =
    lang === 'ru' ? tile.valueRu : lang === 'en' ? tile.valueEn : tile.valueUz;
  const label =
    lang === 'ru' ? tile.labelRu : lang === 'en' ? tile.labelEn : tile.labelUz;
  return (
    <motion.button
      type="button"
      onClick={onPress}
      variants={{ hidden: { opacity: 0, x: -6 }, show: { opacity: 1, x: 0 } }}
      whileTap={{ scale: 0.96 }}
      className="shrink-0 inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full"
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
        boxShadow: `inset 0 0 0 1px ${tile.ring}, 0 4px 12px ${tile.glow}`,
      }}
    >
      <span
        className="inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0"
        style={{
          background: 'rgba(0,0,0,0.25)',
          boxShadow: `inset 0 0 0 1px ${tile.ring}, 0 0 8px ${tile.glow}`,
        }}
      >
        <Icon className={`h-2.5 w-2.5 ${tile.iconTone}`} strokeWidth={2.6} />
      </span>
      <span
        className="text-[10.5px] font-extrabold leading-none number-tabular"
        style={
          tile.valueGradient
            ? {
                background: tile.valueGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }
            : undefined
        }
      >
        {value}
      </span>
      <span className="text-[9.5px] text-text-tertiary whitespace-nowrap leading-none">
        {label}
      </span>
    </motion.button>
  );
}

/* ------------------- TrustInfoSheet ------------------- */
/**
 * Bottom sheet that explains one trust tile in detail. Tapping the backdrop or
 * the close button dismisses it. Animated with a slide-up spring so it feels
 * native rather than a browser modal.
 */
function TrustInfoSheet({
  tile,
  lang,
  onClose,
}: {
  tile: TrustTile | null;
  lang: 'uz' | 'ru' | 'en';
  onClose: () => void;
}) {
  const Icon = tile?.Icon;
  const title =
    tile?.details[lang === 'ru' ? 'titleRu' : lang === 'en' ? 'titleEn' : 'titleUz'] ?? '';
  const lines = tile?.details[lang] ?? [];
  const closeLabel = lang === 'ru' ? 'Понятно' : lang === 'en' ? 'Got it' : 'Tushundim';

  return (
    <AnimatePresence>
      {tile && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 36 }}
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl"
            style={{
              background:
                'linear-gradient(180deg, #1A1A22 0%, #0F0F14 100%)',
              boxShadow:
                `0 -16px 60px ${tile.glow}, 0 -2px 0 ${tile.ring} inset`,
              paddingBottom: 'calc(var(--safe-bottom) + 16px)',
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2.5 pb-1">
              <div className="h-1 w-10 rounded-full bg-white/15" />
            </div>

            <div className="px-5 pt-3">
              {/* Header: glowing icon + title */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'rgba(0,0,0,0.4)',
                    boxShadow: `inset 0 0 0 1px ${tile.ring}, 0 0 24px ${tile.glow}`,
                  }}
                >
                  {Icon && (
                    <Icon className={`h-5 w-5 ${tile.iconTone}`} strokeWidth={2.4} />
                  )}
                </div>
                <h3 className="text-[17px] font-extrabold leading-tight">{title}</h3>
              </div>

              {/* Bullet lines */}
              <ul className="space-y-2.5 mb-5">
                {lines.map((line, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.045, duration: 0.28 }}
                    className="flex gap-2.5 text-[13.5px] leading-snug text-text-secondary"
                  >
                    <span
                      className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                      style={{
                        background: tile.iconTone.includes('amber')
                          ? '#FBBF24'
                          : tile.iconTone.includes('emerald')
                          ? '#34D399'
                          : tile.iconTone.includes('violet')
                          ? '#A78BFA'
                          : '#38BDF8',
                        boxShadow: `0 0 8px ${tile.glow}`,
                      }}
                    />
                    <span>{line}</span>
                  </motion.li>
                ))}
              </ul>

              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-2xl py-3 text-[14px] font-semibold text-white transition-colors"
                style={{
                  background:
                    'linear-gradient(135deg, #2AABEE 0%, #7F77DD 100%)',
                  boxShadow: '0 8px 24px rgba(127,119,221,0.35)',
                }}
              >
                {closeLabel}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ------------------- SectionHeader ------------------- */
function SectionHeader({ title, kicker }: { title: string; kicker?: string }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <h2 className="text-base font-bold tracking-tight">{title}</h2>
        {kicker && (
          <div className="text-[10px] uppercase tracking-widest text-text-secondary mt-0.5">
            {kicker}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------- FooterBadge ------------------- */
function FooterBadge({ lang }: { lang: string }) {
  const t =
    lang === 'ru'
      ? 'Безопасные платежи · Лицензированный сервис'
      : lang === 'en'
      ? 'Secure payments · Licensed service'
      : 'Xavfsiz to‘lov · Litsenziyalangan xizmat';
  return (
    <Link
      to="/trust"
      className="block text-center text-text-tertiary text-[11px] py-4 hover:text-white/70 transition-colors"
    >
      🔒 {t}
    </Link>
  );
}
